import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import configs from "config";
import { CicoAuthResponse, IAddAccount, IBankAccounts, ICicoAuth, ICicoQuote, ICicoQuoteRO, ICicoRate, ICicoRateRo, ITransferPayload, ICicoTransferRo, CicoKYCStatus, IPagination, } from "interfaces";
import ls from "utils/secureStorage";

const _auth = ls.get(configs.CICO_TOKEN_KEY);

export const cicoApi = createApi({
  reducerPath: "cico",
  refetchOnMountOrArgChange: true,
  tagTypes: ['fiat-accounts', 'transfer'],
  baseQuery: fetchBaseQuery({
    // credentials: 'include',
    baseUrl: configs.CICO_BASE_URL,
    prepareHeaders: (headers, _) => {
      // const accessKey = ls.get(configs.ACCESS_KEY);
      // const auth = ls.get(configs.CICO_TOKEN_KEY);

      // headers.set('access-key', 'cc1aa8fa3444822195db');
      // headers.set("FiatConnect-Signature", "application/json");
      headers.set('Authorization', _auth);
      headers.set("Content-Type", "application/json");
      headers.set("Cache-Control", "no-cache, no-store, must-revalidate");

      return headers;
    },
  }),

  endpoints: (builder) => ({
    cicoSignin: builder.mutation({
      query: ({ message, signature, address }: ICicoAuth) => ({
        url: '/auth/dev-login',
        // url: '/auth/login',
        body: {
          message,
          signature,
          address: {
            address
          }
        },
        method: 'POST'
      }),

      transformResponse: (res: CicoAuthResponse) => res
    }),

    cicoQuoteIn: builder.mutation({
      query: ({ country, cryptoAmount, cryptoType, fiatAmount, fiatType, type }: ICicoQuote) => ({
        url: `/quote/${type ? type : 'in'}?acceptAuthInHeaders=true`,
        body: {
          country,
          cryptoAmount,
          cryptoType,
          fiatAmount,
          fiatType,
        },
        method: 'POST'
      }),
      transformResponse: (res: ICicoQuoteRO) => res
    }),

    getCicoRate: builder.query({
      query: ({ from, to }: ICicoRate) => (`/rates/fiat?from=${from}&to=${to}&acceptAuthInHeaders=true`),
      transformResponse: (res: ICicoRateRo) => res
    }),

    /**
     * @description 
     * Initiates a transfer
     * IN = From fiat to Crypto,
     * OUT = From crypto to Fiat
     * @param {ITransferType} params - The parameters for the transfer.
     * @returns {Promise<any>} The result of the transfer.
     */
    initiateTransfer: builder.mutation({
      query: ({ type, fiatAccountId, quoteId }: ITransferPayload) => ({
        url: `/transfer/${type ? type : 'in'}?acceptAuthInHeaders=true`,
        method: 'POST',
        body: {
          fiatAccountId,
          quoteId,
        },
      }),
      invalidatesTags: ['transfer'],
      transformResponse: (res: any) => res
    }),

    transferStatus: builder.query({
      query: (id: string) => (`/transfer/${id}?acceptAuthInHeaders=true`),
      transformResponse: (res: any) => res,
    }),


    getAllCicoTransfer: builder.query({
      query: ({ page, limit }: IPagination) => (`/transfer/own?page=${page}&limit=${limit}&acceptAuthInHeaders=true`),
      transformResponse: (res: ICicoTransferRo) => res,
    }),

    addFiatAccount: builder.mutation({
      query: ({ accountName, accountNumber, institutionName, country }: IAddAccount) => ({
        url: '/accounts?acceptAuthInHeaders=true',
        method: 'POST',
        body: {
          accountName,
          accountNumber,
          institutionName,
          country,
          fiatAccountSchema: 'AccountNumber',
          fiatAccountType: 'bankaccount',
        },
      }),

      invalidatesTags: ['fiat-accounts'],
      transformResponse: (res: any) => res,
    }),

    getCicoFiatAccounts: builder.query({
      query: () => (`/accounts?acceptAuthInHeaders=true`),
      transformResponse: (res: IBankAccounts) => res,
      providesTags: ['fiat-accounts']
    }),

    deleteFiatAccount: builder.mutation({
      query: (id: string) => ({
        url: `/accounts/${id}?acceptAuthInHeaders=true`,
        method: 'DELETE',
        body: {},
      }),

      invalidatesTags: ['fiat-accounts'],
      transformResponse: (res: any) => res,
    }),

    createCicoKyc: builder.mutation({
      query: (body: any) => ({
        url: `/kyc/PersonalDataAndDocuments?acceptAuthInHeaders=true`,
        method: 'POST',
        body: {
          ...body
        }
      }),
      transformResponse: (res: any) => res,
    }),

    getCicoKycStatus: builder.query({
      query: () => (`/kyc/PersonalDataAndDocuments/status?acceptAuthInHeaders=true`),
      transformResponse: (res: CicoKYCStatus) => res,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useCicoSigninMutation,
  useCicoQuoteInMutation,
  useGetCicoRateQuery,

  useAddFiatAccountMutation,
  useGetCicoFiatAccountsQuery,
  useDeleteFiatAccountMutation,

  useInitiateTransferMutation,
  useTransferStatusQuery,
  useGetAllCicoTransferQuery,

  useCreateCicoKycMutation,
  useGetCicoKycStatusQuery,
} = cicoApi;

export default cicoApi;
